import {
  AfterViewInit,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  NgZone,
  OnInit,
  ViewEncapsulation,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { each as _each, find, map as _map, reduce, sortBy } from 'lodash-es';
import { PropertyApiService } from 'apps/account/src/account-shared/services/property-api.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LivePreviewService } from 'apps/account/src/account-shared/services/live-preview.service';
import { NotificationsService } from 'apps/account/src/account-shared/services/notifications.service';
import { DEFAULT_LANGUAGE } from 'apps/account/src/account-shared/services/language-helper.util';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FtLanguageSelectorComponent } from '../contest-sheets/ft-language-selector.component';
import { FieldEditorModalConfig, FtFieldEditorModalComponent } from '../contest-sheets/field-editor-modal.component';
import { FtPendingChangesPanelComponent } from './ft-pending-changes-panel.component';
import { FtDisabledDirective } from './ft-disabled.directive';
import { computeDiffs, revertDiffs, snapshotClone } from '../contest-sheets/native-diff.util';

/**
 * Native port of the AngularJS warm-lead certificate-reminder-email editor — the reminder sibling of
 * {@link FtTouchpointCertificateEmailComponent}, backed by the same controller. Identical except the
 * heading key (flipto.account.ReminderEmailHeading) and a 7-item carousel keyed by the Reminder-suffixed
 * email types. Loads its own data (Property.getEmailsData).
 */
@Component({
    selector: 'ft-touchpoint-certificate-reminder-email-ng',
    imports: [TranslateModule, FtLanguageSelectorComponent, FtFieldEditorModalComponent, FtPendingChangesPanelComponent, FtDisabledDirective],
    encapsulation: ViewEncapsulation.None,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    styleUrls: ['./ft-touchpoint-certificate-reminder-email.component.scss'],
    templateUrl: './ft-touchpoint-certificate-reminder-email.component.html'
})
export class FtTouchpointCertificateReminderEmailComponent implements OnInit, AfterViewInit {
  readonly propertyUuid = input.required<string>();
  readonly touchpointType = input.required<string>();
  readonly emailTypes = input<any>();

  readonly panel = viewChild<FtPendingChangesPanelComponent>('panel');

  /** ftModal phase 2 / WS3: native field-editor modal request + field-level snapshot for cancel-revert. */
  protected readonly fieldEditorConfig = signal<FieldEditorModalConfig | null>(null);
  private fieldSnapshot: any[] | null = null;

  protected readonly model = signal<any>({ emailsData: [], languages: [], selectedLanguage: {} });
  protected readonly selectedEmailData = signal<any>({ languages: undefined, emailType: undefined });
  protected readonly carouselItems = signal<{ key: string; association: any }[]>([]);
  protected readonly carouselIndex = signal(0);

  private snapshotProxy: any;
  private updates: any[] = [];

  // The per-email-type token map (verbatim from EmailsDataController — shared with cert-email).
  private readonly tokensPerType: Record<number, string[]> = {
    11: ['visitor-firstname', 'guest-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-guest-photo', 'link-to-gallery'],
    7: ['friend-firstname', 'guest-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-guest-photo', 'link-to-gallery'],
    1: ['friend-firstname', 'guest-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-gallery', 'contest-content'],
    3: ['friend-firstname', 'guest-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-gallery', 'contest-content'],
    13: ['guest-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-gallery', 'contest-content'],
    22: ['guest-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-gallery', 'contest-content'],
    12: ['visitor-firstname', 'guest-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-guest-photo', 'link-to-gallery'],
    8: ['friend-firstname', 'guest-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-guest-photo', 'link-to-gallery'],
    2: ['friend-firstname', 'guest-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-gallery', 'contest-content'],
    4: ['friend-firstname', 'guest-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-gallery', 'contest-content'],
    23: ['guest-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-gallery', 'contest-content'],
    36: ['traveler-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-gallery', 'contest-content'],
    37: ['traveler-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-gallery', 'contest-content'],
    80: ['guest-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-gallery', 'contest-content', 'incentive-custom-code'],
    81: ['guest-firstname', 'property-city', 'property-name', 'property-shortname', 'link-to-gallery', 'contest-content', 'incentive-custom-code'],
  };

  // The carousel email-type keys, in the reminderEmail.html order (7 reminder types).
  private readonly carouselSpec: { key: string; type: string }[] = [
    { key: 'flipto.account.CertificateEmailCarouselContent.AnonymousPhotoSignupCertificateReminder', type: 'AnonymousPhotoSignupCertificateReminder' },
    { key: 'flipto.account.CertificateEmailCarouselContent.FriendPhotoSignupCertificateReminder', type: 'FriendPhotoSignupCertificateReminder' },
    { key: 'flipto.account.CertificateEmailCarouselContent.FriendPrestayHomepageSignupCertificateReminder', type: 'FriendPrestayHomepageSignupCertificateReminder' },
    { key: 'flipto.account.CertificateEmailCarouselContent.FriendPoststayHomepageSignupCertificateReminder', type: 'FriendPoststayHomepageSignupCertificateReminder' },
    { key: 'flipto.account.CertificateEmailCarouselContent.GuestPoststayPromotionCertificateReminder', type: 'GuestPoststayPromotionCertificateReminder' },
    { key: 'flipto.account.CertificateEmailCarouselContent.GuestPhotoFinalistCertificateReminder', type: 'GuestPhotoFinalistCertificateReminder' },
    { key: 'flipto.account.CertificateEmailCarouselContent.DefaultCertificateReminder', type: 'DefaultCertificateReminder' },
  ];

  // WS5 #10 inc 2t: FULLY drained — livePreview is the native LivePreviewService
  // (sendTestEmail/showTestEmail), `_` the same lodash module instance.
  private readonly property: any = inject(PropertyApiService);
  private readonly livePreview = inject(LivePreviewService);
  private readonly notifications = inject(NotificationsService);

  private readonly translateSvc = inject(TranslateService);
  private readonly zone = inject(NgZone);
  private readonly sanitizer = inject(DomSanitizer);

  ngOnInit(): void {
    this.carouselItems.set(this.carouselSpec.map((s) => ({ key: s.key, association: this.emailTypes()[s.type] })));
    Promise.resolve(this.property.getEmailsData(this.propertyUuid())).then((data: any) => {
      this.zone.run(() => {
        this.model().emailsData = data;
        this.model().languages = _map(this.model().emailsData[0].languages, (l: any) => ({ code: l.code, name: l.name }));
        _each(this.model().emailsData, (d: any) => {
          d.languages = sortBy(d.languages, (lang: any) => (lang.code === 'en' ? '0' : lang.code));
        });
        this.carouselIndex.set(0);
        this.selectByEmailType(this.carouselItems()[0].association);
        this.model().selectedLanguage = find(this.model().languages, { code: DEFAULT_LANGUAGE }) || this.model().languages[0] || {};
        // Data-landed baseline — see ft-touchpoint-certificate-email: a field modal opened
        // before the load would otherwise baseline the EMPTY model (phantom pending changes).
        // liveOverride is REQUIRED — the panel's [live] input is stale until the next CD.
        this.panel()?.setup(this.model().emailsData);
        if (this.fieldEditorConfig()) this.fieldSnapshot = snapshotClone(this.selectedEmailData().languages);
      });
    });
  }

  ngAfterViewInit(): void {
    // Native deferred-save panel in the template; snapshots lazily on first field-open.
  }

  // --- read-view helpers ---

  protected trust(value: any): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(value || '');
  }

  protected langData(field: string): any {
    const lang = find(this.selectedEmailData().languages, { code: this.model().selectedLanguage?.code });
    return lang ? lang[field] : undefined;
  }

  protected get hasContestContentToken(): boolean {
    const tokens = this.tokensPerType[this.selectedEmailData()?.emailType] || [];
    return tokens.indexOf('contest-content') !== -1;
  }

  // --- carousel (native reproduction of ft-carousel) ---

  protected carouselSelect(i: number): void {
    this.carouselIndex.set(i);
    this.selectByEmailType(this.carouselItems()[i].association);
  }
  protected carouselLeft(): void {
    if (this.canGoLeft()) this.carouselSelect(this.carouselIndex() - 1);
  }
  protected carouselRight(): void {
    if (this.canGoRight()) this.carouselSelect(this.carouselIndex() + 1);
  }
  protected canGoLeft(): boolean {
    return this.carouselIndex() > 0;
  }
  protected canGoRight(): boolean {
    return this.carouselIndex() < this.carouselItems().length - 1;
  }

  selectByEmailType(association: any): void {
    this.selectedEmailData.set(find(this.model().emailsData, (e: any) => e.emailType === association) || { languages: undefined, emailType: undefined });
  }

  // --- edit-modal bridges ---

  // ftModal phase 2 / WS3: native shell + native pending-changes panel (same recipe as cert-email).
  protected editSubject(): void {
    this.panel()?.setupIfNeeded();
    this.fieldSnapshot = snapshotClone(this.selectedEmailData().languages);
    this.fieldEditorConfig.set({
      type: 'text',
      datasource: () => this.selectedEmailData().languages,
      fields: [{ name: 'subject' }],
      fieldsOptions: { required: true },
      title: this.translateSvc.instant('flipto.account.Subject'),
      onCancel: () => this.rollbackField(),
      onClose: () => this.afterFieldClose(),
    });
  }

  protected editBody(): void {
    this.panel()?.setupIfNeeded();
    this.fieldSnapshot = snapshotClone(this.selectedEmailData().languages);
    this.fieldEditorConfig.set({
      type: 'html',
      datasource: () => this.selectedEmailData().languages,
      fields: [{ name: 'body' }],
      fieldsOptions: { required: true, tokens: this.tokensPerType[this.selectedEmailData().emailType] || [] },
      title: this.translateSvc.instant('flipto.account.Body'),
      onCancel: () => this.rollbackField(),
      onClose: () => this.afterFieldClose(),
    });
  }

  protected editContestContent(): void {
    this.panel()?.setupIfNeeded();
    this.fieldSnapshot = snapshotClone(this.selectedEmailData().languages);
    this.fieldEditorConfig.set({
      type: 'html',
      datasource: () => this.selectedEmailData().languages,
      fields: [{ name: 'contest_content' }],
      fieldsOptions: { tokens: this.tokensPerType[this.selectedEmailData().emailType] || [] },
      title: this.translateSvc.instant('flipto.account.ContestContent'),
      onCancel: () => this.rollbackField(),
      onClose: () => this.afterFieldClose(),
    });
  }

  private rollbackField(): void {
    if (!this.fieldSnapshot) return;
    revertDiffs(this.selectedEmailData().languages, this.fieldSnapshot, computeDiffs(this.fieldSnapshot, this.selectedEmailData().languages));
  }
  private afterFieldClose(): void {
    this.fieldSnapshot = null;
    this.panel()?.recompute();
  }
  protected onPanelSave(diffs: any[]): void {
    Promise.resolve(this.saveChanges(diffs)).then(() => this.panel()?.commit());
  }
  protected onPanelCancel(): void {
    this.panel()?.recompute();
  }

  /**
   * v1 reminderEmail.html kept a hidden `<ng-form name="emailData">` with a required
   * subject+body input per pending update ({emailType,language} of each diff), and passed
   * `inactive="{{!emailData.$valid}}"` to the panel — Save dims when ANY pending update's
   * language has an empty subject or body (emailsData.controller getUpdates + $watchCollection).
   */
  protected panelInactive(): boolean {
    // Via getUpdates (NOT raw diff paths): the native diff is rooted at the emailsData
    // ARRAY, so deep-diff emits nested kind:'A' changes whose `path` points at the array —
    // reading `c.path[0]` directly skips every one of them and the dim never engages.
    // getUpdates flattens them (flattenChange) into the {emailType, subject, body} updates
    // the v1 hidden `emailData` form validated.
    const panel = this.panel();
    if (!panel) return false;
    const updates = this.getUpdates(panel.currentDiffs());
    return updates.some((u: any) => !u.subject || !u.body);
  }

  // --- live preview (ported; sendTestEmail SENDS a real email — never auto-triggered) ---

  protected sendTestEmail(): void {
    this.livePreview
      .sendTestEmail({ propertyUuid: this.propertyUuid(), emailType: this.selectedEmailData().emailType, languageCode: this.model().selectedLanguage.code })
      .then(
        () => this.notifications.show(this.translateSvc.instant('flipto.account.SendSampleEmailSuccess')),
        () => this.notifications.show(this.translateSvc.instant('flipto.account.SendSampleEmailError')),
      );
  }
  protected showEmailHtml(): void {
    this.livePreview.showTestEmail({ propertyUuid: this.propertyUuid(), emailType: this.selectedEmailData().emailType, languageCode: this.model().selectedLanguage.code });
  }

  // --- save (bridged by the pending-changes panel) ---

  setupSnaphotProxy(proxy: any): void {
    this.snapshotProxy = proxy;
  }

  saveChanges(changes: any[]): any {
    const updates = this.getUpdates(changes);
    return this.property.saveEmailsData(this.propertyUuid(), updates);
  }

  private getUpdates(changes: any[]): any[] {
    // native-diff over the emailsData ARRAY → flatten deep-diff kind:'A' to [i,'languages',j,field]
    // → emailsData index = path[0], languages index = path[2] (see cert-email; lazy snapshot + flatten).
    let updatedData = (changes || [])
      .map((c: any) => flattenChange(c))
      .filter((c: any) => Array.isArray(c.path) && c.path[1] === 'languages' && typeof c.path[0] === 'number' && typeof c.path[2] === 'number')
      .map((c: any) => {
        const chngObj = this.model().emailsData[c.path[0]];
        // angular.extend({}, a, b) ≡ Object.assign for plain objects (inc 2t —
        // drops the window.angular load-order dependency).
        return Object.assign({}, { emailType: chngObj.emailType }, chngObj.languages[c.path[2]]);
      });
    updatedData = reduce(
      updatedData,
      (result: any[], value: any) => {
        const isThere = !!find(result, (r: any) => r.emailType === value.emailType && r.code === value.code);
        if (!isThere) result.push(value);
        return result;
      },
      [],
    );
    updatedData = _map(updatedData, (d: any) => ({
      emailType: d.emailType,
      languageCode: d.code,
      subject: d.subject,
      body: d.body,
      footer: d.footer,
      contestContent: d.contest_content,
    }));
    return updatedData;
  }
}

/** Flatten deep-diff kind:'A' (array) changes into flat index paths ([i,'languages',j,field]). */
function flattenChange(c: any, base: any[] = []): any {
  if (c && c.kind === 'A') {
    return flattenChange(c.item, base.concat(c.path || []).concat([c.index]));
  }
  return Object.assign({}, c, { path: base.concat((c && c.path) || []) });
}
